/* Pick a person from the roster (designs/intake-unified-form.md UF16/UF17):
   the point of contact on an effort, or the platform reviewer. Reads the
   same /api/teams the Teams page serves (the TeamsSource seam), flattened
   to people; any team's people, because a lane lead is not a platform
   role. A person with no Graph id is offered by name and keyed on the
   name — honest, and the same fallback the roster itself uses. */
import { useEffect, useId, useMemo, useRef, useState, type JSX } from "react";
import type { FieldHelp } from "./form-help";
import type { PersonRef } from "../../../shared/capabilities";
import type { Team } from "../../../shared/teams";
import { asJson } from "./api";
import { useDismiss } from "./useDismiss";

export interface RosterPerson extends PersonRef {
  teamName: string;
}

/* pure, DOM-free: every distinct person across the roster, keyed on the
   Graph id where the source recorded one, otherwise on the name */
export const flattenRoster = (teams: Team[]): RosterPerson[] => {
  const seen = new Map<string, RosterPerson>();
  for (const team of teams)
    for (const m of team.members) {
      const userId = m.personId ?? `name:${m.name}`;
      if (!seen.has(userId)) seen.set(userId, { userId, name: m.name, teamName: team.name });
    }
  return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
};

export const rankPeople = (typed: string, people: RosterPerson[]): RosterPerson[] => {
  const q = typed.trim().toLowerCase();
  if (q === "") return people.slice(0, 8);
  return people.filter((p) => p.name.toLowerCase().includes(q)).slice(0, 8);
};

export function PersonPicker({
  label,
  value,
  onChange,
  missing,
  disabled = false,
  help,
}: {
  label: string;
  value: PersonRef | null;
  onChange: (next: PersonRef | null) => void;
  /* the red note shown while nobody is picked — the CALLER words it, because
     what is missing differs: a new form's instruction, a saved effort's
     "not recorded", a reviewer an outcome needs (intake-form-help.md FH8).
     Omitted = the field is optional and shows no note. */
  missing?: string;
  disabled?: boolean;
  /* the field's hint + hover (intake-form-help.md FH4); omitted = none */
  help?: FieldHelp;
}): JSX.Element {
  const [roster, setRoster] = useState<RosterPerson[] | null>(null);
  const [rosterFailed, setRosterFailed] = useState(false);
  const [typed, setTyped] = useState("");
  const [open, setOpen] = useState(false);
  const dismiss = useDismiss(open, setOpen);
  const hintId = useId();
  /* "change" unmounts the button that held focus; the reopened input
     takes it, so the menu's Escape and Tab handling reach the picker
     rather than the drawer behind it (Copilot on #145) */
  const inputRef = useRef<HTMLInputElement>(null);
  const [focusInput, setFocusInput] = useState(false);
  useEffect(() => {
    if (!focusInput) return;
    inputRef.current?.focus();
    setFocusInput(false);
  }, [focusInput]);
  useEffect(() => {
    fetch("/api/teams")
      .then((res) => asJson<Team[]>(res))
      .then((teams) => setRoster(flattenRoster(teams)))
      .catch(() => setRosterFailed(true));
  }, []);
  const ranked = useMemo(() => rankPeople(typed, roster ?? []), [typed, roster]);

  if (value !== null)
    return (
      <div className="capture-field" data-person-picker>
        <span title={help?.more}>{label}</span>
        <div className="picker-picked">
          <span className="picker-chip">{value.name}</span>
          <button
            type="button"
            className="link-inline"
            aria-describedby={help === undefined ? undefined : hintId}
            disabled={disabled}
            onClick={() => {
              onChange(null);
              setOpen(true);
              setFocusInput(true);
            }}
          >
            change
          </button>
        </div>
        {/* the guidance stays once the field is filled (FH4, Copilot on #146) */}
        {help !== undefined && (
          <small className="capture-hint" id={hintId}>
            {help.hint}
          </small>
        )}
      </div>
    );

  return (
    <div className="capture-field" data-person-picker {...dismiss}>
      <label>
        <span title={help?.more}>{label}</span>
        <input
          ref={inputRef}
          value={typed}
          aria-describedby={help === undefined ? undefined : hintId}
          disabled={disabled || rosterFailed}
          placeholder={rosterFailed ? "roster did not load" : "type a name"}
          autoComplete="off"
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setTyped(e.target.value);
            setOpen(true);
          }}
        />
      </label>
      {help !== undefined && (
        <small className="capture-hint" id={hintId}>
          {help.hint}
        </small>
      )}
      {missing !== undefined && (
        <p className="owners-note not-recorded" data-person-missing>
          {missing}
        </p>
      )}
      {rosterFailed && (
        <p className="capture-error">The roster did not load — refresh to retry.</p>
      )}
      {open && roster !== null && (
        <div className="picker-menu" role="listbox" aria-label={`${label} options`}>
          {ranked.map((p) => (
            <button
              type="button"
              key={p.userId}
              role="option"
              aria-selected={false}
              className="picker-option"
              onClick={() => {
                onChange({ userId: p.userId, name: p.name });
                setTyped("");
                setOpen(false);
              }}
            >
              {p.name} <span className="picker-sub">· {p.teamName}</span>
            </button>
          ))}
          {ranked.length === 0 && <div className="kv-note">No one on the roster matches.</div>}
        </div>
      )}
    </div>
  );
}
